import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getAllSermons } from '../../Redux/slices/sermonSlice';
import Header from '../Common/Header';
import Footer from '../Common/Footer';
import { FaSearch, FaClock, FaPlay, FaDownload } from 'react-icons/fa';
import readingImage from '../../Assets/Reading.png';
import prophetessImage from '../../Assets/Prophetess.png';

const Sermons = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { sermons, loading, error } = useSelector((state) => state.sermons);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('all');
  const [filteredSermons, setFilteredSermons] = useState([]);

  useEffect(() => {
    dispatch(getAllSermons());
  }, [dispatch]);

  useEffect(() => {
    if (sermons && sermons.length > 0) {
      let filtered = sermons;

      // Filter by search query
      if (searchQuery.trim()) {
        filtered = filtered.filter(sermon => 
          sermon.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          sermon.preacher?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          sermon.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          sermon.tags?.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
        );
      }

      // Filter by sermon type
      if (selectedType !== 'all') {
        filtered = filtered.filter(sermon => sermon.sermonType === selectedType);
      }

      setFilteredSermons(filtered);
    }
  }, [sermons, searchQuery, selectedType]);

  const handleSermonClick = (sermonId) => {
    navigate(`/sermon/${sermonId}`);
  };

  const featuredSermon = sermons?.find(sermon => sermon.isFeatured) || sermons?.[0];
  const regularSermons = filteredSermons.filter(sermon => sermon._id !== featuredSermon?._id);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const getSermonTypeBadgeColor = (sermonType) => {
    const colors = {
      'Regular': 'bg-blue-600',
      'Series': 'bg-purple-600',
      'Special Event': 'bg-orange-500',
      'Guest Speaker': 'bg-green-600'
    };
    return colors[sermonType] || 'bg-gray-600';
  };

  return (
    <>
      <Header />
      
      {/* Hero Section */}
      <div 
        className="relative bg-cover bg-center h-96 flex items-end"
        style={{ 
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${readingImage})`,
        }}
      >
        <div className="container mx-auto px-6 md:px-12 lg:px-24 pb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">Sermons</h1>
          <p className="text-white text-lg md:text-xl max-w-2xl font-sf-pro">
            Experience life-changing messages that inspire, challenge, and transform hearts through the power of God's Word
          </p>
        </div>
      </div>

      {/* Recent Messages Section */}
      <div className="bg-gray-50 py-16">
        <div className="container mx-auto px-6 md:px-12 lg:px-24">
          <h2 className="text-3xl md:text-4xl font-bold mb-2">Recent Messages</h2>
          <p className="text-gray-600 mb-8 font-sf-pro">Powerful sermons that speak to the heart and transform lives</p>
          
          {/* Search and Filter Section */}
          <div className="flex flex-col md:flex-row gap-4 mb-14">
            <div className="flex-1 relative">
              <FaSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input 
                type="text" 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sermons by title, speaker, or topic" 
                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary font-sf-pro"
              />
            </div>
            <div className="flex gap-2 flex-wrap">
              <button 
                onClick={() => setSelectedType('all')}
                className={`${selectedType === 'all' ? 'bg-primary text-white' : 'bg-white text-gray-700 border border-gray-300'} px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition font-sf-pro`}
              >
                All Sermon
              </button>
              <button 
                onClick={() => setSelectedType('Series')}
                className={`${selectedType === 'Series' ? 'bg-primary text-white' : 'bg-white text-gray-700 border border-gray-300'} px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition font-sf-pro`}
              >
                Sermon series
              </button>
              <button 
                onClick={() => setSelectedType('Special Event')}
                className={`${selectedType === 'Special Event' ? 'bg-primary text-white' : 'bg-white text-gray-700 border border-gray-300'} px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition font-sf-pro`}
              >
                Special Events
              </button>
              <button 
                onClick={() => setSelectedType('Guest Speaker')}
                className={`${selectedType === 'Guest Speaker' ? 'bg-primary text-white' : 'bg-white text-gray-700 border border-gray-300'} px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition font-sf-pro`}
              >
                Guest Speaker
              </button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="mt-4 text-gray-600">Loading sermons...</p>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
              {error}
            </div>
          )}

          {/* Featured Message Card */}
          {!loading && featuredSermon && (
            <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-24 mt-24">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-0">
                <div className="relative bg-gray-200 h-64 lg:h-auto">
                  <img 
                    src={featuredSermon.thumbnailUrl || readingImage} 
                    alt={featuredSermon.title} 
                    className="w-full h-full object-cover" 
                  />
                </div>
                <div className="p-8">
                  <div className="flex items-center gap-2 mb-4">
                    <span className="bg-primary text-white text-xs px-3 py-1 rounded-full font-medium font-sf-pro">Featured Message</span>
                    <span className="text-gray-600 text-sm font-sf-pro">{formatDate(featuredSermon.date)}</span>
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold mb-4">{featuredSermon.title}</h3>
                  <p className="text-gray-600 mb-6 font-sf-pro">
                    {featuredSermon.description}
                  </p>
                  <div className="flex items-center gap-4 mb-6">
                    <img src={prophetessImage} alt={featuredSermon.preacher} className="w-10 h-10 rounded-full object-cover" />
                    <span className="font-medium font-sf-pro">{featuredSermon.preacher}</span>
                    {featuredSermon.duration && (
                      <div className="flex items-center gap-1 text-gray-600">
                        <FaClock className="text-sm" />
                        <span className="text-sm font-sf-pro">{featuredSermon.duration}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <button 
                      onClick={() => handleSermonClick(featuredSermon._id)}
                      className="bg-primary text-white px-6 py-3 rounded-lg font-medium hover:bg-opacity-90 transition font-sf-pro"
                    >
                      Watch now
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Sermon Grid */}
          {!loading && regularSermons.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {regularSermons.map((sermon) => (
                <button 
                  key={sermon._id}
                  onClick={() => handleSermonClick(sermon._id)} 
                  className="bg-white rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow text-left"
                >
                  <div className="relative">
                    <span className={`absolute top-4 left-4 ${getSermonTypeBadgeColor(sermon.sermonType)} text-white text-xs px-3 py-1 rounded-full font-medium font-sf-pro z-10`}>
                      {sermon.sermonType}
                    </span>
                    <div className="relative h-48 bg-gray-200">
                      <img 
                        src={sermon.thumbnailUrl || readingImage} 
                        alt={sermon.title} 
                        className="w-full h-full object-cover" 
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 hover:bg-opacity-50 transition">
                        <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
                          <FaPlay className="text-primary text-xl ml-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-gray-600 text-sm mb-3 font-sf-pro">
                      <span>{formatDate(sermon.date)}</span>
                      {sermon.duration && (
                        <>
                          <span>•</span>
                          <span>{sermon.duration}</span>
                        </>
                      )}
                    </div>
                    <h3 className="text-xl font-bold mb-3">{sermon.title}</h3>
                    <p className="text-gray-600 text-sm mb-4 font-sf-pro line-clamp-2">
                      {sermon.description}
                    </p>
                    <div className="flex items-center gap-3">
                      <img src={prophetessImage} alt={sermon.preacher} className="w-8 h-8 rounded-full object-cover" />
                      <span className="text-sm font-medium font-sf-pro">{sermon.preacher}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {/* No Results */}
          {!loading && filteredSermons.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-600 text-lg">No sermons found matching your criteria.</p>
              <button 
                onClick={() => {
                  setSearchQuery('');
                  setSelectedType('all');
                }}
                className="mt-4 text-primary hover:underline"
              >
                Clear filters
              </button>
            </div>
          )}

          {/* Results Count */}
          {!loading && filteredSermons.length > 0 && (
            <div className="flex justify-center mt-12">
              <p className="text-gray-600 font-sf-pro">
                Showing {filteredSermons.length} sermon{filteredSermons.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Never Miss a Message Section */}
      <div className="py-16 px-6 md:px-12 lg:px-24">
        <div className="bg-primary text-white py-16 px-8 md:px-16 rounded-2xl">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Never Miss a Message</h2>
            <p className="text-white text-lg mb-8 font-sf-pro">
              Subscribe to receive our latest sermons directly in your inbox
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center items-center max-w-2xl mx-auto">
              <input 
                type="email" 
                placeholder="Enter your email address" 
                className="w-full sm:flex-1 px-6 py-3 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-white font-sf-pro"
              />
              <button className="w-full sm:w-auto bg-black text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-900 transition font-sf-pro whitespace-nowrap">
                Subscribe now
              </button>
            </div>
            <p className="text-white text-sm mt-4 font-sf-pro">
              We respect your privacy and will never share your information.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default Sermons;